import { DisclaimerDataProps } from 'components/GeneralDisclaimer';
import { ScreenerDataProps } from 'components/PrePurchaseScreener';
import { graphql, useStaticQuery } from 'gatsby';
import { useMemo } from 'react';
import { ProductDataType } from 'utils/types';

type PageScreenerData = {
  product: {
    slug: string;
  };
  screener: ScreenerDataProps;
  disclaimer: DisclaimerDataProps;
};

// Map - slug with screener and disclaimer
type ProductWithScreener = Record<string, PageScreenerData>;

type PageScreenerResult = {
  nodes: PageScreenerData[];
};

const getAllScreenerData = (
  allContentfulPdpPage: PageScreenerResult,
): ProductWithScreener => {
  const productsWithScreener: ProductWithScreener = {};
  allContentfulPdpPage?.nodes
    .filter((page) => page?.product?.slug)
    .forEach((page) => {
      productsWithScreener[page.product.slug] = {
        product: page.product,
        screener: page.screener,
        disclaimer: page.disclaimer,
      };
    });

  return productsWithScreener;
};

export default function useScreenerData(): ProductWithScreener {
  const { allContentfulPdpPage } = useStaticQuery(graphql`
    query {
      allContentfulPdpPage {
        nodes {
          product {
            slug
          }
          disclaimer {
            title
            body {
              raw
            }
            callToAction {
              url
              text
            }
          }
          screener {
            options {
              items
              label
              value
            }
            callToActionText
            description
            title
          }
        }
      }
    }
  `);

  const allProductScreenerData = getAllScreenerData(allContentfulPdpPage);

  return allProductScreenerData;
}

/**
 * Hydrate the given products with screener data if available
 * @param products
 * @returns
 */
export const useHydrateProductsWithScreenerData = (
  products: ProductDataType[],
) => {
  const pageScreenerData = useScreenerData();

  const productsWithScreenerData = useMemo(
    () =>
      products
        .filter((product) => product)
        .map((product) => {
          const productScreenerData = pageScreenerData[product?.slug];
          return {
            ...product,
            screener: productScreenerData?.screener,
            disclaimer: productScreenerData?.disclaimer,
          };
        }),
    [pageScreenerData, products],
  );

  return productsWithScreenerData;
};
